import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAllDealsAdmin,
  updateDealStatus,
  deleteDeal as apiDeleteDeal,
  withAreaInfo,
  isRemovedDeal,
} from '../../services/apiService';
import { STATUS } from '../../services/shopState';
import { logActivity } from '../../services/adminMockData';
import { DEAL_CATEGORIES } from '../../constants/categories';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit', month: 'short',
  });
}

const STATUS_OPTIONS = ['actief', 'gepauzeerd', 'verlopen', 'gepland'];

export default function AdminDealsPage() {
  // Deals live in the backend. This page used to read the localStorage deal
  // store, which only ever held demo/offline data, so retailer deals never
  // appeared. GET /deals/all returns every deal whatever its status.
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [permConfirm, setPermConfirm] = useState(null);   // deal | null
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  // Removed deals (DELETE /deals/:id → active = false) stay in the database
  // for statistics, but are hidden here unless explicitly asked for.
  const [showRemoved, setShowRemoved] = useState(false);
  const [toast, setToast] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setDeals(await withAreaInfo(await getAllDealsAdmin()));
    } catch (err) {
      setDeals([]);
      if (err?.status === 401) {
        setLoadError('Je admin-sessie is verlopen. Log opnieuw in.');
      } else if (err?.status === 403) {
        setLoadError('Dit account heeft geen adminrechten voor deals.');
      } else {
        setLoadError(err?.message || 'Kon deals niet laden.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('walkin-deals-change', refresh);
    return () => window.removeEventListener('walkin-deals-change', refresh);
  }, [refresh]);

  const visibleDeals = useMemo(
    () => (showRemoved ? deals : deals.filter((d) => !isRemovedDeal(d))),
    [deals, showRemoved]
  );
  const removedCount = useMemo(() => deals.filter(isRemovedDeal).length, [deals]);
  const shopNames = useMemo(
    () => Array.from(new Set(visibleDeals.map((d) => d.shopName).filter(Boolean))).sort(),
    [visibleDeals]
  );
  const areaNames = useMemo(
    () => Array.from(new Set(visibleDeals.map((d) => d.area).filter(Boolean))).sort(),
    [visibleDeals]
  );
  const categories = DEAL_CATEGORIES;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visibleDeals.filter((d) => {
      if (q) {
        const hay = `${d.title} ${d.shopName} ${d.area}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (areaFilter && d.area !== areaFilter) return false;
      if (shopFilter && d.shopName !== shopFilter) return false;
      if (statusFilter && (d.status || 'actief') !== statusFilter) return false;
      if (categoryFilter && d.category !== categoryFilter) return false;
      return true;
    });
  }, [visibleDeals, query, areaFilter, shopFilter, statusFilter, categoryFilter]);

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((d) => d.id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Bulk actions run against the backend. Failures are reported rather than
  // silently written to a local store the list no longer reads.
  const bulkSetStatus = async (status, actionLabel) => {
    if (selected.size === 0) return;
    const ids = [...selected];
    const results = await Promise.allSettled(
      ids.map((id) => updateDealStatus(id, status))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    logActivity(actionLabel, { count: ids.length - failed });
    setSelected(new Set());
    await refresh();
    if (failed) {
      showToast(`${failed} van ${ids.length} deals kon niet worden bijgewerkt`, 'error');
    } else {
      showToast(`${ids.length} deals bijgewerkt`);
    }
  };

  const bulkPause = () => bulkSetStatus(STATUS.PAUSED, 'Bulk pauze deals');
  const bulkActivate = () => bulkSetStatus(STATUS.ACTIVE, 'Bulk activeer deals');
  // Removing is DELETE /deals/:id — there is no 'verwijderd' deal status in
  // the database, so the old status PATCH was rejected and nothing happened.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = [...selected];
    const results = await Promise.allSettled(ids.map((id) => apiDeleteDeal(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    logActivity('Bulk verwijder deals', { count: ids.length - failed });
    setSelected(new Set());
    setBulkDeleteConfirm(false);
    await refresh();
    showToast(
      failed ? `${failed} van ${ids.length} deals kon niet worden verwijderd`
             : `🗑️ ${ids.length} deals verwijderd`,
      'error'
    );
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const permanentDelete = async (deal) => {
    try {
      await apiDeleteDeal(deal.id);
    } catch (err) {
      showToast(err?.message || 'Verwijderen mislukt', 'error');
      return;
    }
    logActivity('Deal verwijderd', {
      deal: deal.title,
      shop: deal.shopName,
      id: deal.id,
    });
    setPermConfirm(null);
    setDetail(null);
    await refresh();
    showToast('🗑️ Deal verwijderd', 'error');
  };

  const cleanupAllInactive = async () => {
    const stale = deals.filter((d) => !isRemovedDeal(d) && d.status === STATUS.EXPIRED);
    const results = await Promise.allSettled(
      stale.map((d) => apiDeleteDeal(d.id))
    );
    const removed = results.filter((r) => r.status === 'fulfilled').length;
    logActivity('Bulk opruiming deals', { removed });
    setCleanupConfirm(false);
    await refresh();
    showToast(`🗑️ ${removed} van ${stale.length} verlopen deals verwijderd`, 'error');
  };

  const inactiveCount = useMemo(
    () => deals.filter((d) => !isRemovedDeal(d) && d.status === STATUS.EXPIRED).length,
    [deals]
  );

  const setDealStatus = async (deal, status) => {
    try {
      await updateDealStatus(deal.id, status);
    } catch (err) {
      showToast(err?.message || 'Status wijzigen mislukt', 'error');
      return;
    }
    logActivity('Deal status gewijzigd', { deal: deal.title, status });
    await refresh();
    showToast('Status bijgewerkt');
  };

  return (
    <div>
      {toast && (
        <div className={`admin__toast admin__toast--${toast.type}`}>{toast.message}</div>
      )}

      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Deals</h1>
          <p className="admin-page-sub">
            {loading
              ? 'Deals laden…'
              : `${filtered.length} van ${visibleDeals.length} deals`}
          </p>
        </div>
        {inactiveCount > 0 && (
          <button
            className="admin-btn admin-btn--danger"
            onClick={() => setCleanupConfirm(true)}
          >
            🗑️ Verlopen opruimen ({inactiveCount})
          </button>
        )}
      </header>

      {loadError && (
        <div className="admin-card" style={{ textAlign: 'center', padding: '32px 20px' }}>
          <span style={{ fontSize: 48, display: 'block' }}>⚠️</span>
          <h3 style={{ margin: '10px 0 6px', color: '#991B1B' }}>Kon deals niet laden</h3>
          <p style={{ color: '#6B7280', margin: '0 0 16px' }}>{loadError}</p>
          <button className="admin-btn admin-btn--orange" onClick={refresh}>
            Opnieuw proberen
          </button>
        </div>
      )}

      <div className="admin-toolbar">
        <input
          className="admin-input admin-input--grow"
          type="search"
          placeholder="Zoek titel, winkel, gebied…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="admin-select" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
          <option value="">Alle gebieden</option>
          {areaNames.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="admin-select" value={shopFilter} onChange={(e) => setShopFilter(e.target.value)}>
          <option value="">Alle winkeliers</option>
          {shopNames.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="admin-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Alle statussen</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="admin-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">Alle categorieën</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {removedCount > 0 && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#6B7280' }}>
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(e) => setShowRemoved(e.target.checked)}
            />
            Toon verwijderde ({removedCount})
          </label>
        )}
      </div>

      {selected.size > 0 && (
        <div style={{
          background: '#1A3E5C', color: '#fff', padding: '10px 14px',
          borderRadius: 10, marginBottom: 12, display: 'flex',
          gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <strong>{selected.size} geselecteerd</strong>
          <button className="admin-btn admin-btn--small admin-btn--ghost" onClick={bulkActivate}>
            Activeren
          </button>
          <button className="admin-btn admin-btn--small admin-btn--ghost" onClick={bulkPause}>
            Pauzeren
          </button>
          <button className="admin-btn admin-btn--small admin-btn--danger" onClick={() => setBulkDeleteConfirm(true)}>
            Verwijderen
          </button>
          <button className="admin-link" style={{ color: '#fff', marginLeft: 'auto' }}
            onClick={() => setSelected(new Set())}>
            Selectie wissen
          </button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={toggleAll}
                />
              </th>
              <th>Deal</th>
              <th>Winkelier</th>
              <th>Gebied</th>
              <th>Status</th>
              <th>Aangemaakt</th>
              <th>Geldig tot</th>
              <th>Views / Inlev. / Conv.</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="admin-empty">Geen deals gevonden</td></tr>
            )}
            {filtered.map((d) => {
              const status = d.status || 'actief';
              const views = d.views || 0;
              const reds = d.redemptions || 0;
              const conv = views > 0 ? Math.round((reds / views) * 100) : 0;
              return (
                <tr key={d.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleOne(d.id)}
                    />
                  </td>
                  <td>
                    <button className="admin-link" onClick={() => setDetail(d)}>
                      {d.emoji} {d.title}
                    </button>
                  </td>
                  <td>{d.shopName}</td>
                  <td>{d.area}</td>
                  <td>
                    <span className={`admin-badge admin-badge--${status}`}>{status}</span>
                  </td>
                  <td>{formatDate(d.createdAt)}</td>
                  <td>{formatDate(d.expiresAt)}</td>
                  <td>{views} / {reds} / {conv}%</td>
                  <td>
                    <div className="admin-table__actions">
                      {isRemovedDeal(d) ? null : status === 'actief' ? (
                        <button
                          className="admin-btn admin-btn--small admin-btn--ghost"
                          onClick={() => setDealStatus(d, STATUS.PAUSED)}
                        >
                          Pauzeer
                        </button>
                      ) : (
                        <button
                          className="admin-btn admin-btn--small admin-btn--ghost"
                          onClick={() => setDealStatus(d, STATUS.ACTIVE)}
                        >
                          Activeer
                        </button>
                      )}
                      {!isRemovedDeal(d) && (
                        <button
                          className="admin-btn admin-btn--small admin-btn--danger"
                          onClick={() => setPermConfirm(d)}
                        >
                          Verwijder
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="admin-modal" role="dialog" onClick={() => setDetail(null)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <h3 className="admin-modal__title">
              {detail.emoji} {detail.title}
            </h3>
            <p className="admin-modal__text" style={{ margin: 0 }}>
              <strong>{detail.shopName}</strong> · {detail.area}
            </p>
            {detail.description && (
              <p className="admin-modal__text">{detail.description}</p>
            )}
            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Categorie</span>
                <div>{detail.category || '—'}</div>
              </div>
              <div className="admin-field">
                <span className="admin-field__label">Status</span>
                <span className={`admin-badge admin-badge--${detail.status || 'actief'}`}>
                  {detail.status || 'actief'}
                </span>
              </div>
            </div>
            <div className="admin-form-row">
              <div className="admin-field">
                <span className="admin-field__label">Gebiedscode</span>
                <div><span className="admin-mono">{detail.areaCode || '—'}</span></div>
              </div>
              <div className="admin-field">
                <span className="admin-field__label">Verloopt</span>
                <div>{formatDate(detail.expiresAt)}</div>
              </div>
            </div>
            <div className="admin-grid">
              <div className="admin-stat">
                <span className="admin-stat__label">Views</span>
                <span className="admin-stat__value">{detail.views || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat__label">Inleveringen</span>
                <span className="admin-stat__value">{detail.redemptions || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat__label">Gedeeld</span>
                <span className="admin-stat__value">{detail.shares || 0}</span>
              </div>
            </div>
            {Array.isArray(detail.usedCodes) && detail.usedCodes.length > 0 && (
              <div className="admin-field">
                <span className="admin-field__label">Gebruikte codes</span>
                <div style={{ fontSize: 12 }}>
                  {detail.usedCodes.map((c, i) => (
                    <span key={i} className="admin-mono" style={{ marginRight: 6 }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="admin-modal__actions">
              {!isRemovedDeal(detail) && (
                <button
                  className="admin-btn admin-btn--danger"
                  onClick={() => setPermConfirm(detail)}
                >
                  🗑️ Verwijderen
                </button>
              )}
              <button className="admin-btn" onClick={() => setDetail(null)}>Sluiten</button>
            </div>
          </div>
        </div>
      )}

      {/* PERMANENT DELETE CONFIRM ----------------------------------------- */}
      {permConfirm && (
        <div className="admin-modal" role="dialog" onClick={() => setPermConfirm(null)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Deal verwijderen?</h3>
            <p className="admin-modal__text">
              De deal verdwijnt direct uit de app voor bezoekers en uit het
              overzicht van de winkelier.
            </p>
            <div style={{
              background: '#F8FAFC', padding: 12, borderRadius: 10,
              fontSize: 13, lineHeight: 1.5,
            }}>
              <div><strong>Deal:</strong> {permConfirm.emoji} {permConfirm.title}</div>
              <div><strong>Winkelier:</strong> {permConfirm.shopName}</div>
              <div style={{ color: '#9CA3AF', fontSize: 12, marginTop: 6 }}>
                Views en inleveringen blijven bewaard voor statistieken en facturatie.
              </div>
            </div>
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setPermConfirm(null)}>
                Annuleren
              </button>
              <button
                className="admin-btn admin-btn--danger"
                onClick={() => permanentDelete(permConfirm)}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDeleteConfirm && (
        <div className="admin-modal" role="dialog" onClick={() => setBulkDeleteConfirm(false)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">{selected.size} deals verwijderen?</h3>
            <p className="admin-modal__text">
              De geselecteerde deals verdwijnen direct uit de app en uit de
              overzichten van de winkeliers.
            </p>
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setBulkDeleteConfirm(false)}>
                Annuleren
              </button>
              <button className="admin-btn admin-btn--danger" onClick={bulkDelete}>
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BULK CLEANUP CONFIRM --------------------------------------------- */}
      {cleanupConfirm && (
        <div className="admin-modal" role="dialog" onClick={() => setCleanupConfirm(false)}>
          <div className="admin-modal__card" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal__title">Alle verlopen deals opruimen?</h3>
            <p className="admin-modal__text">
              Dit verwijdert <strong>{inactiveCount}</strong> verlopen deals uit
              de overzichten.
            </p>
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setCleanupConfirm(false)}>
                Annuleren
              </button>
              <button className="admin-btn admin-btn--danger" onClick={cleanupAllInactive}>
                {inactiveCount} deals opruimen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
